import FileUploader from "@via/components/file-uploader/file-uploader";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import Modal from "@via/components/modal";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor";
import { NOTIFICATION } from "@via/context/notifications-context";
import { useNotifications } from "@via/hooks/useNotifications";
import { useAddCustomerBoundary } from "@via/pages/account/admin/admin.api.tsx";
import { ModalSize } from "@via/utils/constants";
import { formatSpreadsheetCellValue } from "@via/pages/data/premium-dataset/utils/helper";
import { BoundaryKind, InsertCustomerBoundaryRequestBody } from "common";
import { useState } from "react";

interface AddCustomerBoundaryProps {
  isOpen: boolean;
  onClose: () => void;
}

// A bare `Omit<CustomerBoundaryCSV, "id">` does not distribute over the gcp/aws union and would
// collapse to their common fields only, so this reuses the already distributive request body type
// rather than re-deriving it. See DistributiveOmit in common/src/api/admin.ts.
type CustomerBoundaryRow = InsertCustomerBoundaryRequestBody;

// Columns each vendor's row must carry, keyed by cloudVendor. These are the fields that became
// required server side (Joi `.required()` in backend/src/routes/admin/customer-boundary.ts) after
// the CSV template operators use was already in circulation, so a template built before the change
// is silently missing them. Checked here, before the row is ever sent, so the operator is told
// which column and which row rather than waiting on a generic Joi failure from the round trip.
const REQUIRED_COLUMNS_BY_VENDOR: Record<string, string[]> = {
  [BoundaryKind.GCP]: ["gcpNetwork", "gcpMasterDowndir"],
  // AWS has no entry on purpose. `awsMasterDowndir` is optional server side, so an AWS row that
  // omits it or leaves the cell blank must upload rather than be refused here. A vendor with no
  // entry is not checked at all, which is why this is an absent key rather than an empty array.
};

/**
 * Returns the name of the first required column missing (absent or blank) from a parsed CSV row,
 * or undefined when the row is fine.
 *
 * Each vendor is asked only for its own columns. The backend forbids the other kind's outright, so
 * a GCP column is not something an AWS row is short of. This was a GCP only check when the two GCP
 * columns became required, which left the AWS row that later gained a required column falling
 * through to a generic server side failure instead.
 *
 * A row whose cloudVendor is unset or unrecognised is not checked: the vendor is what decides which
 * columns apply, and the backend rejects an unrecognised one on its own.
 *
 * Exported so the "Row N: missing required column" message can be unit tested directly, without
 * driving the file upload, the spreadsheet editor, or a network call.
 */
export const findMissingRequiredColumn = (
  file: Record<string, unknown>
): string | undefined => {
  const vendor = file["cloudVendor"];
  const required =
    typeof vendor === "string" ? REQUIRED_COLUMNS_BY_VENDOR[vendor] : undefined;
  return required?.find(column => {
    const value = file[column];
    return typeof value !== "string" || !value.trim();
  });
};

const AddCustomerBoundary = ({ isOpen, onClose }: AddCustomerBoundaryProps) => {
  const { setNotification } = useNotifications();
  const [columnLabels, setColumnLabels] = useState<string[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());

  const {
    mutateAsync: addCustomerBoundary,
    isPending: isAddingBoundaryLoading,
  } = useAddCustomerBoundary();

  const parseCSVLine = (line: string, separator: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i += 2;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === separator && !inQuotes) {
        // Found separator outside quotes
        result.push(current.trim());
        current = "";
        i++;
      } else {
        current += char;
        i++;
      }
    }

    result.push(current.trim());
    return result;
  };

  const handleFileUpload = (files: File[]) => {
    const file = files[0];
    const reader = new FileReader();

    reader.onload = event => {
      const content = (event.target?.result as string) || "";
      if (!content.trim()) {
        console.warn("File is empty.");
        return;
      }

      const firstLine = content.split("\n")[0].replace(/\r/g, "") || "";
      const separator = firstLine.includes("\t") ? "\t" : ",";

      const lines = content.split("\n").map(line => line.replace(/\r/g, ""));

      // Parse each line properly
      const parsedData = lines.map(line => parseCSVLine(line, separator));

      // First row as column labels
      const labels = parsedData[0]?.map(label => label.trim()) || [];

      const spreadsheetRows: { value: string }[][] = [];
      const rows = parsedData?.slice(1) || [];

      if (rows.length > 0) {
        rows.forEach(row => {
          // check if row is full of null, don't add it
          if (row.every(cell => !cell || cell.trim() === "")) {
            return;
          }
          const formattedRow = row.map(
            cell => formatSpreadsheetCellValue(cell?.replace(/\r/g, "")) // Remove any carriage returns
          );
          // The index a blank row would have had in the source file (rows.forEach's own index)
          // is not this row's index in spreadsheetRows, since blank rows are dropped rather than
          // kept as placeholders: spreadsheetRows is compacted. changedRows is later read as an
          // index straight into spreadsheetData (== spreadsheetRows), so it must record the
          // position this row actually lands at, spreadsheetRows.length right before the push,
          // not the row's position in the uncompacted source. Using the source index here let a
          // CSV with a blank row in the middle either read the wrong row or run past the end of
          // spreadsheetData and throw, taking down the whole upload with a message that named
          // neither the row nor the column.
          const compactedRowIndex = spreadsheetRows.length;
          spreadsheetRows.push(formattedRow);
          setChangedRows(prev => new Set(prev).add(compactedRowIndex));
        });
      } else {
        // Create one empty row if no rows exist
        const emptyRow = labels.map(() => ({ value: "" }));
        spreadsheetRows.push(emptyRow);
        setChangedRows(prev => new Set(prev).add(0));
      }

      setColumnLabels(labels);
      setSpreadsheetData(spreadsheetRows);
    };

    reader.onerror = error => {
      console.error("Error reading file:", error);
    };

    reader.readAsText(file); // Read file content
  };

  const updateSpreadsheetData = (
    data: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => {
    setSpreadsheetData(data);
    const changedRowIndices =
      rowIndices && rowIndices.length > 0
        ? rowIndices
        : rowIndex !== undefined
          ? [rowIndex]
          : [];
    if (changedRowIndices.length === 0) {
      return;
    }
    setChangedRows(prev => new Set([...prev, ...changedRowIndices]));
  };

  const processError = (err: any, index: number, internalErrors: string[]) => {
    if (
      err?.response?.data?.details &&
      Array.isArray(err.response.data.details)
    ) {
      const errorMessages = err.response.data.details.map(errorDetail => {
        return `${index + 1}. column: ${errorDetail.path?.[0] ?? "unknown field"}: ${errorDetail.message ?? "unknown error"}`;
      });
      internalErrors.push(...errorMessages);
    } else if (err?.response?.data?.message) {
      setNotification({
        id: "premium-dataset-file-creation-failed",
        message: "Dataset File Creation Failed",
        details: err.response.data?.message,
        variant: NOTIFICATION.ERROR,
      });
    } else {
      internalErrors.push(
        `Error processing file ${index + 1}: ${err?.message ?? "unknown error"}`
      );
    }
  };

  const handleConfirm = async () => {
    // Carries each row's position in this upload batch alongside its payload, so a row skipped
    // here for a missing column does not shift the row numbers processError reports for the rows
    // that do get submitted (a plain `files: CustomerBoundaryRow[]` would renumber from 0 after
    // any skip, so "Row 2" from this check and "2." from a later network error could name two
    // different rows).
    const files: Array<{ position: number; boundary: CustomerBoundaryRow }> =
      [];
    // Explicitly typed: pushed to from inside the forEach callback below, and an untyped `[]`
    // seals to `never[]` the moment a nested closure is the first place that pushes to it.
    const internalErrors: string[] = [];
    setErrors([]);

    try {
      // Only process rows that have been modified
      const changedRowsArray = Array.from(changedRows);

      changedRowsArray.forEach((rowIndex, position) => {
        const row = spreadsheetData[rowIndex];

        // Convert spreadsheetData to files array
        const file: CustomerBoundaryRow | Record<string, unknown> = {};
        row.forEach((cell, colIndex) => {
          const label = columnLabels[colIndex];
          if (label) {
            const value = cell.value?.toString() || "";
            file[label] =
              value.startsWith("{") || value.startsWith("[")
                ? JSON.parse(value)
                : value;
          }
        });

        // Row number is this row's position within the upload batch, one indexed: not the literal
        // line number in the source CSV, since blank rows are dropped before this point.
        const missingColumn = findMissingRequiredColumn(file);
        if (missingColumn) {
          internalErrors.push(
            `Row ${position + 1}: missing required column "${missingColumn}"`
          );
          return; // Do not submit a row already known to fail validation.
        }

        files.push({ position, boundary: file as CustomerBoundaryRow });
      });

      for (let i = 0; i < files.length; i++) {
        try {
          const result = await addCustomerBoundary(files[i].boundary);
          if ("error" in result) {
            processError(result.error, files[i].position, internalErrors);
          }
        } catch (error) {
          processError(error, files[i].position, internalErrors);
        }
      }

      setChangedRows(new Set()); // Clear changed rows after processing

      if (internalErrors.length) {
        setErrors(internalErrors);
      } else {
        onClose();
      }
    } catch (error: any) {
      setNotification({
        id: "error-transforming-data",
        message: "Error transforming data",
        details:
          error?.message || "Please ensure the data is in the correct format.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Add Boundary"
      size={ModalSize.Large}
      showButtons={false}>
      <>
        <FileUploader
          onUpload={handleFileUpload}
          maxFiles={1}
          isLoading={false}
          progressPercentageList={[]}
          acceptedTypes={[".tsv", ".csv"]}
          containerClassName="w-full"
        />
        <div className="my-4 max-h-[250px] overflow-auto">
          <MessageBanner messages={errors} type={BannerType.Error} />
        </div>
        {spreadsheetData.length > 0 && (
          <div className="mb-5">
            <SpreadsheetEditor
              columnLabels={columnLabels}
              data={spreadsheetData}
              onChange={updateSpreadsheetData}
              disableContextMenu
            />
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button
            id="cancel-button"
            type="button"
            className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={onClose}>
            Cancel
          </button>
          <button
            id="create-button"
            type="button"
            disabled={isAddingBoundaryLoading}
            onClick={e => {
              e.preventDefault();
              handleConfirm();
            }}
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Create
          </button>
        </div>
      </>
    </Modal>
  );
};

export default AddCustomerBoundary;
